import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Product } from 'src/app/common/models/product';
import { Text } from 'src/common/components/typography/Text';
import { LeasingCalculator } from 'src/app/product/components/ProductPageSummary/LeasingCalculator';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeOfferSelector,
  availableOffersSelector,
} from 'src/app/product/state/products/productsSelectors';
import { InternalCalculator } from 'src/app/product/components/ProductPageSummary/InternalCalculator';
import { useCalculatorInitialOffer } from 'src/app/product/hooks/useCalculatorInitialOffer';
import { useGetArtefaktaLeasingOffers } from 'src/app/product/hooks/useGetArtefaktaLeasingOffers';
import { useFindOfferOnCustomerTypeChange } from 'src/app/product/hooks/useFindOfferOnCustomerTypeChange';
import { useOnFinancingTypeChange } from 'src/app/product/hooks/useOnFinancingTypeChange';
import { FinancingType, Offer } from 'src/common/models/offer';
import { productsActions } from 'src/app/product/state/products/productsActions';
import { findOfferWithParams } from 'src/app/product/services/helpers';

export interface BaseInternalCalculatorProps {
  product: Product;
}

export const BaseInternalCalculator: FC<BaseInternalCalculatorProps> = ({
  product,
}) => {
  const activeOffer = useSelector(activeOfferSelector);
  const availableOffers = useSelector(availableOffersSelector);
  const reduxDispatch = useDispatch();

  const initialOffer = useCalculatorInitialOffer();
  const artefaktaLeasingOffers = useGetArtefaktaLeasingOffers({
    initialOffer,
    product,
  });

  useFindOfferOnCustomerTypeChange({
    initialOffer,
    leasingOffers: artefaktaLeasingOffers,
    product,
  });

  const handleFinancingTypeButtonChange = useOnFinancingTypeChange({
    leasingOffers: artefaktaLeasingOffers,
    product,
  });

  const offer = activeOffer || initialOffer;

  const lockedLeasingOffersByLabel = useMemo(() => {
    const locked = availableOffers.filter(
      (o) =>
        o.financingType === FinancingType.Leasing && o.leasingLabel && !o.external
    );
    return locked.reduce((acc, o) => {
      const label = o.leasingLabel!;
      if (!acc[label]) acc[label] = [];
      acc[label].push(o);
      return acc;
    }, {} as Record<string, Offer[]>);
  }, [availableOffers]);

  const lockedLeasingLabels = useMemo(
    () => Object.keys(lockedLeasingOffersByLabel),
    [lockedLeasingOffersByLabel]
  );

  const hasStandardLeasing = useMemo(
    () => availableOffers.some((o) => o.external?.type === 'artefakta4internal'),
    [availableOffers]
  );

  const [activeLeasingLabel, setActiveLeasingLabel] = useState<string | null>(
    offer?.leasingLabel ?? null
  );

  useEffect(() => {
    setActiveLeasingLabel(offer?.leasingLabel ?? null);
  }, [offer?.id]);

  const leasingOffers = activeLeasingLabel
    ? lockedLeasingOffersByLabel[activeLeasingLabel] ?? []
    : artefaktaLeasingOffers;

  const handleLeasingLabelChange = useCallback(
    (label: string | null) => {
      setActiveLeasingLabel(label);
      const pool = label
        ? lockedLeasingOffersByLabel[label] ?? []
        : artefaktaLeasingOffers;
      if (pool.length > 0) {
        if (label) {
          // switching to locked leasing — match params
          const best = findOfferWithParams(
            { financingType: FinancingType.Leasing, customerType: offer?.customerType },
            {
              initialPayment: offer?.initialPayment,
              contractPeriod: offer?.contractPeriod,
              buyoutPrice: offer?.buyoutPrice,
            }
          )(pool, pool[0]);
          reduxDispatch(productsActions.setActiveOffer(best));
        } else {
          // switching to standard leasing — don't match locked params, just pick first
          reduxDispatch(productsActions.setActiveOffer(pool[0]));
        }
      }
    },
    [lockedLeasingOffersByLabel, artefaktaLeasingOffers, offer, reduxDispatch]
  );

  if (offer == null) {
    return <Text>Brak ofert</Text>;
  }

  const isLeasing =
    offer.external?.type === 'artefakta4internal' ||
    (offer.financingType === FinancingType.Leasing && !!offer.leasingLabel);

  return (
    <>
      {isLeasing ? (
        <LeasingCalculator
          activeOffer={offer}
          leasingOffers={leasingOffers}
          onFinancingTypeChange={handleFinancingTypeButtonChange}
          product={product}
          activeLeasingLabel={activeLeasingLabel}
          onLeasingLabelChange={handleLeasingLabelChange}
          hasStandardLeasing={hasStandardLeasing}
          lockedLeasingLabels={lockedLeasingLabels}
        />
      ) : (
        <InternalCalculator
          activeOffer={offer}
          onFinancingTypeChange={handleFinancingTypeButtonChange}
          product={product}
        />
      )}
    </>
  );
};
